import { useState, useEffect } from 'react';
import { classAPI, coachingSessionAPI, coachingProgramAPI, coachingProgramInstanceAPI, amenityAPI, groupPlayAPI } from '../services/api';
import { DashboardBooking, DashboardBookingsData } from '../types/dashboard';
import { parseDateOnlyString, isDateOnly } from '../utils/dateUtils';

const useDashboardBookings = () => {
  const [bookingsData, setBookingsData] = useState<DashboardBookingsData>({
    todaysBookings: [],
    upcomingBookings: [],
    loading: true,
    error: undefined
  });

  const fetchAllBookings = async () => {
    try {
      setBookingsData(prev => ({ ...prev, loading: true, error: undefined }));

      // Get the currently selected member profile from localStorage
      const selectedMemberProfileString = localStorage.getItem('selectedMemberProfile');
      if (!selectedMemberProfileString) {
        setBookingsData({
          todaysBookings: [],
          upcomingBookings: [],
          loading: false,
          error: undefined
        });
        return;
      }

      const selectedMemberProfile = JSON.parse(selectedMemberProfileString);
      const currentMemberProfileId = selectedMemberProfile?.id;

      if (!currentMemberProfileId) {
        setBookingsData({
          todaysBookings: [],
          upcomingBookings: [],
          loading: false,
          error: undefined
        });
        return;
      }

      const today = new Date();
      const allBookings: DashboardBooking[] = [];

      // Fetch class bookings
      try {
        const classBookingsResponse = await classAPI.getUserBookings();
        const classBookings = classBookingsResponse.data;
        
        classBookings.forEach((booking: any) => {
          // Extract data from ClassInstance or fallback to booking data
          const classInstance = booking.classInstance;
          const parentClass = classInstance?.parentClass;

          // Get the session date from ClassInstance
          const bookingDate = classInstance?.sessionDate || booking.sessionDate || booking.bookingDate;

          if (bookingDate) {
            const dashboardBooking = {
              id: booking.id,
              type: 'class' as const,
              title: classInstance?.name || parentClass?.name || booking.className || booking.name || '',
              date: bookingDate,
              startTime: classInstance?.startTime || booking.startTime || '',
              endTime: classInstance?.endTime || booking.endTime || '',
              venue: classInstance?.venue || parentClass?.venue || booking.venue,
              instructor: classInstance?.instructor || parentClass?.instructorName || booking.instructorName || booking.instructor,
              status: booking.status?.toLowerCase() || '',
              cost: classInstance?.costOverride || parentClass?.oneTimeCost || booking.cost,
              notes: booking.notes,
              rawData: booking
            };

            allBookings.push(dashboardBooking);

          }
        });
      } catch (error) {
        console.warn('Failed to fetch class bookings:', error);
      }

      // Fetch coaching session bookings
      try {
        const coachingBookingsResponse = await coachingSessionAPI.getUserBookings();
        const coachingBookings = coachingBookingsResponse.data;
        
        coachingBookings.forEach((booking: any) => {
          const bookingDate = booking.coachingSession?.sessionDate || booking.sessionDate;
          if (bookingDate) {
            allBookings.push({
              id: booking.id,
              type: 'coaching-session',
              title: booking.coachingSession?.name || booking.sessionName || 'Coaching Session',
              date: bookingDate,
              startTime: booking.startTime,
              endTime: booking.endTime,
              venue: booking.coachingSession?.venue || booking.venue,
              coachName: booking.coachingSession?.coachName || booking.coachName,
              status: booking.status?.toLowerCase() || 'confirmed',
              cost: booking.totalCost,
              notes: booking.notes,
              rawData: booking
            });
          }
        });
      } catch (error) {
        console.warn('Failed to fetch coaching session bookings:', error);
      }

      // Fetch coaching program enrollments and their instances
      try {
        const programEnrollmentsResponse = await coachingProgramAPI.getUserEnrollments();
        const programEnrollments = programEnrollmentsResponse.data;
        
        for (const enrollment of programEnrollments) {
          // Skip cancelled/dropped enrollments
          const enrollmentStatus = enrollment.status?.toUpperCase();
          if (enrollmentStatus === 'DROPPED' || enrollmentStatus === 'CANCELLED') {
            continue;
          }

          const program = enrollment.coachingProgram || enrollment.program;
          if (!program) continue;

          // For each enrollment, fetch upcoming program instances
          try {
            const programId = program.id;
            const today = new Date().toISOString().split('T')[0];
            const futureDate = new Date();
            futureDate.setDate(futureDate.getDate() + 90); // Get instances for next 90 days
            const futureDateStr = futureDate.toISOString().split('T')[0];

            const instancesResponse = await coachingProgramAPI.getProgramInstances(programId, {
              startDate: today,
              endDate: futureDateStr,
              includeEnrollments: 'true'
            });
            
            const instances = instancesResponse.data || [];
            
            // Add each upcoming instance as a separate booking
            instances.forEach((instance: any) => {
              // Skip cancelled or inactive instances
              if (instance.status?.toUpperCase() === 'CANCELLED' || instance.active === false) {
                return;
              }

              const instanceDate = instance.sessionDate;
              if (instanceDate) {
                allBookings.push({
                  id: `program-instance-${instance.id}`,
                  type: 'coaching-program',
                  title: instance.name || program.name || 'Coaching Program',
                  date: instanceDate,
                  startTime: instance.startTime || program.recurringPattern?.startTime || '00:00:00',
                  endTime: instance.endTime || program.recurringPattern?.endTime || '01:00:00',
                  venue: instance.venue || program.venue,
                  coachName: instance.coachName || program.coachName,
                  status: enrollment.status?.toLowerCase() || 'enrolled',
                  cost: instance.costOverride || program.sessionCost,
                  notes: instance.notes || enrollment.notes || program.notes,
                  rawData: {
                    ...instance,
                    enrollment,
                    programId: program.id,
                    enrollmentId: enrollment.id
                  }
                });
              }
            });

            // If no instances found or for programs without instances, show the enrollment itself
            if (instances.length === 0 && program.startDate) {
              allBookings.push({
                id: enrollment.id,
                type: 'coaching-program',
                title: program.name || 'Coaching Program',
                date: program.startDate,
                startTime: program.recurringPattern?.startTime || program.sessionTime || '00:00:00',
                endTime: program.recurringPattern?.endTime || '01:00:00',
                venue: program.venue,
                coachName: program.coachName,
                status: enrollment.status?.toLowerCase() || 'enrolled',
                cost: enrollment.amountPaid || enrollment.totalCost || program.totalCost,
                notes: enrollment.notes || program.notes,
                rawData: enrollment
              });
            }
          } catch (instanceError) {
            console.warn(`Failed to fetch instances for program ${program.id}:`, instanceError);
            // Fallback: show the enrollment itself
            if (program.startDate) {
              allBookings.push({
                id: enrollment.id,
                type: 'coaching-program',
                title: program.name || 'Coaching Program',
                date: program.startDate,
                startTime: program.recurringPattern?.startTime || program.sessionTime || '00:00:00',
                endTime: program.recurringPattern?.endTime || '01:00:00',
                venue: program.venue,
                coachName: program.coachName,
                status: enrollment.status?.toLowerCase() || 'enrolled',
                cost: enrollment.amountPaid || enrollment.totalCost || program.totalCost,
                notes: enrollment.notes || program.notes,
                rawData: enrollment
              });
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch coaching program enrollments:', error);
      }

      // Fetch coaching program instance enrollments (drop-in bookings)
      try {
        const instanceEnrollmentsResponse = await coachingProgramInstanceAPI.getUserEnrollments();
        const instanceEnrollments = instanceEnrollmentsResponse.data;
        
        instanceEnrollments.forEach((enrollment: any) => {
          const instance = enrollment.coachingProgramInstance;
          if (instance && instance.sessionDate) {
            allBookings.push({
              id: enrollment.id,
              type: 'coaching-program',
              title: instance.name || instance.parentProgram?.name || 'Coaching Program',
              date: instance.sessionDate,
              startTime: instance.startTime || '00:00:00',
              endTime: instance.endTime || '01:00:00',
              venue: instance.venue,
              coachName: instance.coachName,
              status: enrollment.status?.toLowerCase() || 'enrolled',
              cost: enrollment.totalCost || instance.costOverride,
              notes: enrollment.notes || instance.notes,
              rawData: {
                ...enrollment,
                instanceId: instance.id,
                programId: instance.parentProgram?.id
              }
            });
          }
        });
      } catch (error) {
        console.warn('Failed to fetch coaching program instance enrollments:', error);
      }

      // Fetch amenity bookings
      try {
        const amenityBookingsResponse = await amenityAPI.getUserBookings();
        const amenityBookings = amenityBookingsResponse.data;
        
        amenityBookings.forEach((booking: any) => {
          // bookingDate is in YYYY-MM-DD format
          // startTime and endTime are in HH:MM:SS format
          const bookingDate = booking.bookingDate;
          if (bookingDate) {
            allBookings.push({
              id: booking.id,
              type: 'amenity',
              title: booking.amenity?.name || 'Amenity Booking',
              date: bookingDate,
              startTime: booking.startTime || '00:00:00',
              endTime: booking.endTime || '01:00:00',
              venue: booking.amenity?.location || 'Facility',
              hostName: booking.memberName, // Include host name for amenity bookings
              status: booking.status?.toLowerCase() || 'confirmed',
              cost: booking.bookingFee || booking.cost || booking.totalCost,
              memberAmountPaid: booking.memberAmountPaid, // What the member actually paid
              notes: booking.notes,
              rawData: booking
            });
          }
        });
      } catch (error) {
        console.warn('Failed to fetch amenity bookings:', error);
      }

      // Fetch group play bookings
      try {
        const groupPlayBookingsResponse = await groupPlayAPI.getUserBookings();
        const groupPlayBookings = groupPlayBookingsResponse.data;
        
        groupPlayBookings.forEach((booking: any) => {
          // The instance data is nested under 'instance', not 'groupPlayInstance'
          const instance = booking.instance;
          const parentGroupPlay = instance?.parentGroupPlay;
          const bookingDate = instance?.sessionDate || booking.sessionDate;
          
          if (bookingDate) {
            const dashboardBooking = {
              id: booking.id,
              type: 'group-play' as const,
              title: instance?.name || parentGroupPlay?.name || 'Group Play',
              date: bookingDate,
              startTime: instance?.startTime || '00:00:00',
              endTime: instance?.endTime || '01:00:00',
              venue: instance?.venues?.[0]?.name || parentGroupPlay?.venues?.[0]?.name,
              status: booking.status?.toLowerCase() || 'confirmed',
              cost: booking.totalCost || instance?.memberFeeOverride || parentGroupPlay?.memberSessionFee,
              notes: booking.notes,
              rawData: booking
            };
            allBookings.push(dashboardBooking);
          }
        });
      } catch (error) {
        console.warn('Failed to fetch group play bookings:', error);
      }

      // Sort bookings by date and time
      allBookings.sort((a, b) => {
        // Parse dates properly - date is in YYYY-MM-DD format
        const dateTimeA = `${a.date}T${(a.startTime || '00:00:00').split(/[+-]/)[0]}`;
        const dateTimeB = `${b.date}T${(b.startTime || '00:00:00').split(/[+-]/)[0]}`;
        const dateA = new Date(dateTimeA);
        const dateB = new Date(dateTimeB);
        return dateA.getTime() - dateB.getTime();
      });

      const todaysBookings = allBookings.filter(booking => {
        const bookingDate = isDateOnly(booking.date)
          ? parseDateOnlyString(booking.date)
          : new Date(booking.date);

        // Include all future and today's bookings (not just next 7 days)
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const isInDateRange = bookingDate >= todayStart && booking.status !== 'cancelled';
        
        if (!isInDateRange) {
          return false;
        }

        // For today's bookings, filter out past times
        const isBookingToday = bookingDate.getFullYear() === today.getFullYear() &&
                               bookingDate.getMonth() === today.getMonth() &&
                               bookingDate.getDate() === today.getDate();
        
        if (isBookingToday && booking.startTime) {
          try {
            // Strip timezone suffix and parse just the time part
            const timePart = booking.startTime.split(/[+-]/)[0]; // Get just "HH:MM:SS"
            const [hours, minutes] = timePart.split(':').map(Number);
            const currentHours = today.getHours();
            const currentMinutes = today.getMinutes();
            
            // Only include if booking time is in the future
            const isFutureTime = hours > currentHours || (hours === currentHours && minutes > currentMinutes);
            return isFutureTime;
          } catch (err) {
            console.error('Time parsing error for booking:', err);
            return true; // Include if time parsing fails
          }
        }
        
        return true;
      });

      const upcomingBookings = allBookings.filter(booking => {
        const bookingDate = isDateOnly(booking.date)
          ? parseDateOnlyString(booking.date)
          : new Date(booking.date);
        return bookingDate > today && booking.status !== 'cancelled';
      })
        .sort((a, b) => {
          // Sort by date first, then by time
          const dateA = isDateOnly(a.date) ? parseDateOnlyString(a.date) : new Date(a.date);
          const dateB = isDateOnly(b.date) ? parseDateOnlyString(b.date) : new Date(b.date);

          const dateDiff = dateA.getTime() - dateB.getTime();
          if (dateDiff !== 0) return dateDiff;

          // If same date, sort by start time
          const timeA = new Date(`2000-01-01T${a.startTime}`);
          const timeB = new Date(`2000-01-01T${b.startTime}`);
          return timeA.getTime() - timeB.getTime();
        })
        .slice(0, 5); // Limit to next 5 upcoming bookings

      setBookingsData({
        todaysBookings,
        upcomingBookings,
        loading: false,
        error: undefined
      });

    } catch (error) {
      console.error('Error fetching dashboard bookings:', error);
      setBookingsData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load bookings'
      }));
    }
  };

  useEffect(() => {
    fetchAllBookings();
  }, []);

  return {
    ...bookingsData,
    refetch: fetchAllBookings
  };
};

export default useDashboardBookings;